import { ActionIcon, Button, Menu, Tooltip } from "@mantine/core";
import {
  IconDots,
  IconFileText,
  IconHeadphones,
  IconPencil,
  IconTrash,
  IconVideo,
} from "@tabler/icons-react";
import type { ReactNode } from "react";
import type { SermonRow } from "../sermon-queries";

const MONTH = new Intl.DateTimeFormat("en-US", { month: "short", timeZone: "UTC" });

/** A day as a calendar leaf: "OCT", "11", "2026". The date is a plain day, read as UTC so no zone moves it. */
function leaf(day: string): { month: string; date: string; year: string } {
  const when = new Date(`${day}T00:00:00Z`);
  return {
    month: MONTH.format(when).toUpperCase(),
    date: String(when.getUTCDate()),
    year: String(when.getUTCFullYear()),
  };
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter((w) => /^[A-Za-z]/.test(w))
    .slice(-2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}

function MediaMark({ label, icon }: { readonly label: string; readonly icon: ReactNode }) {
  return (
    <Tooltip label={label}>
      <span className="ws-sermon__media-mark" role="img" aria-label={label}>
        {icon}
      </span>
    </Tooltip>
  );
}

/**
 * The sermons as a list of cards (studio design): the day as a calendar leaf, the title and scripture, the speaker and
 * the series, what media it carries, whether it is published, and its actions. The same actions as the classic table,
 * named the same way.
 */
export function SermonCards({
  sermons,
  speakerName,
  seriesTitle,
  linkOf,
  editable,
  onEdit,
  onTogglePublish,
  onDelete,
}: {
  readonly sermons: readonly SermonRow[];
  readonly speakerName: (id: string | null) => string;
  readonly seriesTitle: (id: string | null) => string;
  readonly linkOf: (sermon: SermonRow) => string | null;
  readonly editable: boolean;
  readonly onEdit: (sermon: SermonRow) => void;
  readonly onTogglePublish: (sermon: SermonRow) => void;
  readonly onDelete: (sermon: SermonRow) => void;
}) {
  return (
    <ul className="ws-sermons" aria-label="Sermons">
      {sermons.map((s) => {
        const day = leaf(s.preachedOn);
        const speaker = speakerName(s.speakerId);
        const series = seriesTitle(s.seriesId);
        const link = linkOf(s);
        return (
          <li key={s.id} className="ws-sermon">
            <span className="ws-sermon__leaf" aria-hidden>
              <span className="ws-sermon__month">{day.month}</span>
              <span className="ws-sermon__date">{day.date}</span>
              <span className="ws-sermon__year">{day.year}</span>
            </span>
            <span className="ws-sermon__main">
              <span className="ws-sermon__title">{s.title}</span>
              <span className="how-visually-hidden">, preached {s.preachedOn}</span>
              {s.scripture.length === 0 ? null : (
                <span className="ws-sermon__scripture">{s.scripture.join("; ")}</span>
              )}
              <span className="ws-sermon__meta">
                {speaker === "" ? null : (
                  <span className="ws-sermon__speaker">
                    <span className="ws-sermon__avatar" aria-hidden>
                      {initials(speaker)}
                    </span>
                    {speaker}
                  </span>
                )}
                {series === "" ? null : <span className="ws-sermon__series">{series}</span>}
              </span>
            </span>
            <span className="ws-sermon__side">
              <span className="ws-sermon__media">
                {s.videoUrl === null ? null : (
                  <MediaMark label="Video" icon={<IconVideo size={16} stroke={1.8} />} />
                )}
                {s.audioPath === null && s.audioUrl === null ? null : (
                  <MediaMark label="Audio" icon={<IconHeadphones size={16} stroke={1.8} />} />
                )}
                {s.notes === null ? null : (
                  <MediaMark label="Notes" icon={<IconFileText size={16} stroke={1.8} />} />
                )}
              </span>
              <span className="ws-card__status" data-state={s.isPublished ? "published" : "draft"}>
                <span className="ws-dot" aria-hidden />
                {s.isPublished ? "Published" : "Draft"}
              </span>
              <span className="ws-sermon__actions">
                {s.isPublished && link !== null ? (
                  <Button
                    component="a"
                    href={link}
                    target="_blank"
                    rel="noreferrer"
                    size="compact-sm"
                    variant="subtle"
                    aria-label={`View ${s.title} on the website (opens in a new tab)`}
                  >
                    View
                  </Button>
                ) : null}
                {editable ? (
                  <>
                    <Button
                      size="compact-sm"
                      variant="default"
                      leftSection={<IconPencil size={14} />}
                      aria-label={`Edit ${s.title}`}
                      onClick={() => {
                        onEdit(s);
                      }}
                    >
                      Edit
                    </Button>
                    <Menu position="bottom-end">
                      <Menu.Target>
                        <ActionIcon variant="subtle" aria-label={`More for ${s.title}`}>
                          <IconDots size={16} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item
                          onClick={() => {
                            onTogglePublish(s);
                          }}
                        >
                          {s.isPublished ? "Unpublish" : "Publish"}
                        </Menu.Item>
                        <Menu.Item
                          color="red"
                          leftSection={<IconTrash size={14} />}
                          onClick={() => {
                            onDelete(s);
                          }}
                        >
                          Delete
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </>
                ) : null}
              </span>
            </span>
          </li>
        );
      })}
    </ul>
  );
}
